/**
 * Pure helpers for deriving how a session is identified to the user.
 * Precedence: user-set session name > linked case ID > raw session ID.
 */

import { METADATA_KEYS } from '../constants';

export type SessionIdentifierSource = 'name' | 'caseId' | 'sessionId';

export interface SessionIdentifier {
  source: SessionIdentifierSource;
  value: string;
}

const firstNonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/**
 * The single source of truth for which identifier represents a session.
 * Returns the winning value and which field it came from.
 */
export function getSessionIdentifier(
  metadata: Record<string, any> | null | undefined,
  sessionId: string,
): SessionIdentifier {
  const name = firstNonEmpty(metadata?.[METADATA_KEYS.SESSION_NAME]);
  if (name) return { source: 'name', value: name };

  const caseId = firstNonEmpty(metadata?.[METADATA_KEYS.CASE_ID]);
  if (caseId) return { source: 'caseId', value: caseId };

  return { source: 'sessionId', value: sessionId };
}

/**
 * The formatted title shown on a session card. A user-given name displays as-is
 * (replacing the session ID); a case ID or the raw ID keep their labels.
 */
export function getSessionDisplayTitle(
  metadata: Record<string, any> | null | undefined,
  sessionId: string,
): string {
  const { source, value } = getSessionIdentifier(metadata, sessionId);
  if (source === 'name') return value;
  if (source === 'caseId') return `Case ID: ${value}`;
  return `Session ${value}`;
}

/**
 * Whether a session matches a free-text search of the session history.
 * Matches (case-insensitively) the session name, case ID, or creator alias.
 * The raw session ID is only searchable when it is what the list displays —
 * i.e. when there is no name or case ID to show in its place.
 * An empty query matches everything.
 */
export function matchesSessionSearch(
  session: { session_id: string; created_by?: string; metadata?: Record<string, any> | null },
  query: string,
): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;

  const searchableFields = [
    session.metadata?.[METADATA_KEYS.SESSION_NAME],
    session.metadata?.[METADATA_KEYS.CASE_ID],
    session.created_by,
  ];

  if (getSessionIdentifier(session.metadata, session.session_id).source === 'sessionId') {
    searchableFields.push(session.session_id);
  }

  return searchableFields.some((value) => typeof value === 'string' && value.toLowerCase().includes(q));
}
